/**
 * Figma `57:2`, "Onde você quer trabalhar?" -- its own card rendering
 * (`OrganisationCard`), not the popover's flat `SwitcherList`
 * (`owner-decisions-align-57-2-07`): a popover row has no room for a card's
 * header and campaign rows, and the two surfaces are the same data, not the
 * same layout. Matching the frame, there is no search field here
 * (`owner-decisions-align-57-2-10`).
 *
 * The frame also draws a credit balance, a 7-day consumption figure, a
 * campaign row's state/member count/reach, and a "Convites" rail card, all
 * rendered below (`OrganisationFigures` per card, the row figures in
 * `OrganisationCard` itself, `InvitationsRail` under the list) -- calling any
 * of them "unbacked" was only ever true of `/control/me` itself, not of this
 * route: `OrganisationEntry` carries the first pair
 * (`owner-decisions-align-57-2-03`), `CampaignEntry` the row figures
 * (`owner-decisions-align-57-2-04`), and `GET /invitations` already backed
 * the rail (`owner-decisions-align-57-2-01`).
 *
 * Unmounted, this page has no `AppShell` above it to own the failed state the
 * way the popover's does, so it renders `error.body` itself.
 */

import type { ReactNode } from 'react';

import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';

import { Page, PageHeader } from '../../../chrome/page';
import { useBreadcrumbLeaf } from '../../../chrome/shell';
import { LoadingState } from '../../../chrome/state';
import { Avatar, AvatarFallback } from '../../../ui/avatar';
import { Card } from '../../../ui/card';
import { useMe } from '../session';
import { InvitationsRail } from './InvitationsRail';
import { OrganisationCard } from './OrganisationCard';
import { sw } from './messages';

/** Same `Card` shell and header row as `OrganisationCard`, so the list's last
 *  card reads as one of them rather than a distinct control. */
function CreateOrganisationCard(): ReactNode {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <Link
        to="/comecar"
        className="flex items-center gap-3 px-6 py-4 font-medium hover:bg-muted/50"
      >
        <Avatar>
          <AvatarFallback kind="brand" aria-hidden>
            <Plus aria-hidden className="size-4" />
          </AvatarFallback>
        </Avatar>
        {sw('page.createOrganisation')}
      </Link>
    </Card>
  );
}

export function SwitcherPage(): ReactNode {
  const me = useMe();
  useBreadcrumbLeaf(sw('page.heading'));

  return (
    <Page>
      <PageHeader title={sw('page.heading')} />
      {me.isPending ? (
        <LoadingState />
      ) : me.error ? (
        <p className="text-muted-foreground p-6 text-center text-sm">{sw('error.body')}</p>
      ) : (
        <div className="flex flex-col gap-4">
          {me.data.organisations.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">{sw('empty')}</p>
          ) : (
            me.data.organisations.map((organisation) => (
              <OrganisationCard key={organisation.tenant_id} organisation={organisation} />
            ))
          )}
          <CreateOrganisationCard />
          <InvitationsRail />
        </div>
      )}
    </Page>
  );
}
